// MARK://dhk8/admin-shell/components/DataTable
// Tabla genérica PRESENTACIONAL para el admin RBAC (Gustavo 2026-06-22).
// El padre maneja TODO el fetch: este componente solo pinta `rows` con sus
// `columns`, dispara callbacks de orden/página y muestra estados loading/vacío.
// - Header sticky, filas hover + zebra opcional, accesible (th scope="col").
// - Paginación server-side (Anterior/Siguiente + "página X de Y" + total) cuando
//   llegan `total` y `onPageChange`.
// - Orden por columna clickeable (▲▼) vía `sort`/`onSortChange` (sortable).
// - Estilos propios en <style> con clases `nui-*` (NO toca styles.css ni .adm-table).
// - Numéricos: usa align="right" en columnas de cifras; el render del padre debe
//   poner --font-mono/tabular-nums (igual que el resto del shell).
import type { ReactNode } from 'react'

export type SortDir = 'asc' | 'desc'
export interface SortState {
  key: string
  dir: SortDir
}

export interface DataTableColumn<T> {
  key: string
  header: ReactNode
  /** Render personalizado de la celda; por defecto `(row as any)[key]`. */
  render?: (row: T) => ReactNode
  sortable?: boolean
  align?: 'left' | 'center' | 'right'
  /** Ancho CSS de la columna (p.ej. '120px', '20%'). */
  width?: string
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  getRowKey: (row: T) => string | number
  loading?: boolean
  emptyText?: ReactNode
  onRowClick?: (row: T) => void
  /** Cebra (zebra striping) en filas pares. */
  zebra?: boolean
  // ── Paginación server-side (todos opcionales; el footer aparece si hay total+onPageChange) ──
  page?: number
  pageSize?: number
  total?: number
  onPageChange?: (page: number) => void
  // ── Orden controlado ──
  sort?: SortState | null
  onSortChange?: (sort: SortState) => void
  /** Slot arriba de la tabla (p.ej. <FilterBar/>). */
  toolbar?: ReactNode
}

const SKELETON_ROWS = 6

export default function DataTable<T>({
  columns,
  rows,
  getRowKey,
  loading,
  emptyText = 'Sin resultados.',
  onRowClick,
  zebra,
  page,
  pageSize,
  total,
  onPageChange,
  sort,
  onSortChange,
  toolbar,
}: DataTableProps<T>) {
  const clickable = typeof onRowClick === 'function'
  const colCount = columns.length

  // Footer de paginación solo si el padre da los datos server-side.
  const hasPaging = typeof total === 'number' && typeof onPageChange === 'function'
  const curPage = page && page > 0 ? page : 1
  const perPage = pageSize && pageSize > 0 ? pageSize : 0
  const totalPages = hasPaging && perPage > 0 ? Math.max(1, Math.ceil((total as number) / perPage)) : 1
  const canPrev = hasPaging && curPage > 1
  const canNext = hasPaging && curPage < totalPages

  const onHeaderClick = (col: DataTableColumn<T>) => {
    if (!col.sortable || !onSortChange) return
    const nextDir: SortDir = sort && sort.key === col.key && sort.dir === 'asc' ? 'desc' : 'asc'
    onSortChange({ key: col.key, dir: nextDir })
  }

  const cellAlign = (a?: 'left' | 'center' | 'right') => a ?? 'left'

  return (
    <div className="nui-table-wrap">
      <style>{NUI_TABLE_CSS}</style>

      {toolbar != null && <div className="nui-table__toolbar">{toolbar}</div>}

      <div className="nui-table__scroll">
        <table className="nui-table" role="table">
          <thead>
            <tr>
              {columns.map((col) => {
                const active = !!sort && sort.key === col.key
                const sortable = !!col.sortable && !!onSortChange
                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={
                      'nui-table__th' +
                      (sortable ? ' nui-table__th--sortable' : '') +
                      (active ? ' nui-table__th--active' : '')
                    }
                    style={{ textAlign: cellAlign(col.align), width: col.width }}
                    aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className="nui-table__sortbtn"
                        onClick={() => onHeaderClick(col)}
                        style={{ justifyContent: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start' }}
                      >
                        <span>{col.header}</span>
                        <span className="nui-table__sortarrow" aria-hidden="true">
                          {active ? (sort!.dir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, r) => (
                <tr key={`sk-${r}`} className="nui-table__row nui-table__row--skeleton">
                  {columns.map((col) => (
                    <td key={col.key} className="nui-table__td" style={{ textAlign: cellAlign(col.align) }}>
                      <span className="nui-table__skel" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td className="nui-table__empty" colSpan={colCount}>
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={getRowKey(row)}
                  className={
                    'nui-table__row' +
                    (clickable ? ' nui-table__row--clickable' : '') +
                    (zebra && idx % 2 === 1 ? ' nui-table__row--zebra' : '')
                  }
                  onClick={clickable ? () => onRowClick!(row) : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  role={clickable ? 'button' : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onRowClick!(row)
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((col) => (
                    <td key={col.key} className="nui-table__td" style={{ textAlign: cellAlign(col.align), width: col.width }}>
                      {col.render ? col.render(row) : ((row as Record<string, unknown>)[col.key] as ReactNode)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {hasPaging && (
        <div className="nui-table__foot">
          <span className="nui-table__count">
            {(total as number).toLocaleString()} {total === 1 ? 'resultado' : 'resultados'}
          </span>
          <div className="nui-table__pager">
            <button
              type="button"
              className="nui-table__pagebtn"
              onClick={() => canPrev && onPageChange!(curPage - 1)}
              disabled={!canPrev}
            >
              Anterior
            </button>
            <span className="nui-table__pageinfo">
              página {curPage} de {totalPages}
            </span>
            <button
              type="button"
              className="nui-table__pagebtn"
              onClick={() => canNext && onPageChange!(curPage + 1)}
              disabled={!canNext}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Estilos self-contained. Clases `nui-*`, valores del scale --pos-*. Curvas
// redondeadas, border-driven elevation, sin edge strips. Header sticky.
const NUI_TABLE_CSS = `
.nui-table-wrap {
  display: flex; flex-direction: column;
  background: var(--pos-surface);
  border: 1px solid var(--pos-separator-strong);
  border-radius: var(--pos-radius-lg);
  box-shadow: var(--pos-shadow-1);
  overflow: hidden;
}
.nui-table__toolbar {
  padding: var(--pos-space-3);
  border-bottom: 1px solid var(--pos-separator);
}
.nui-table__scroll { width: 100%; overflow-x: auto; }
.nui-table {
  width: 100%; border-collapse: collapse;
  font-family: var(--font-sans);
  font-size: var(--pos-text-sm);
  color: var(--pos-fg);
}
.nui-table__th {
  position: sticky; top: 0; z-index: 1;
  background: var(--pos-elevated);
  color: var(--pos-fg-secondary);
  font-size: var(--pos-text-xs);
  font-weight: var(--pos-weight-semibold);
  text-transform: uppercase;
  letter-spacing: var(--pos-tracking-wide, 0.04em);
  padding: var(--pos-space-2) var(--pos-space-3);
  white-space: nowrap;
  border-bottom: 1px solid var(--pos-separator-strong);
}
.nui-table__th--active { color: var(--pos-fg); }
.nui-table__sortbtn {
  display: inline-flex; align-items: center; gap: 6px;
  width: 100%; background: transparent; border: none; padding: 0; margin: 0;
  font: inherit; color: inherit; text-transform: inherit; letter-spacing: inherit;
  cursor: pointer; -webkit-tap-highlight-color: transparent;
}
.nui-table__sortarrow {
  font-size: 9px; line-height: 1; opacity: .8;
  font-family: var(--font-mono, var(--font-sans));
}
.nui-table__th--sortable:not(.nui-table__th--active) .nui-table__sortarrow { opacity: .35; }
.nui-table__row {
  border-bottom: 1px solid var(--pos-separator);
  transition: background var(--pos-duration-fast) var(--pos-ease-out);
}
.nui-table tbody tr:last-child { border-bottom: none; }
.nui-table__row--zebra { background: color-mix(in oklch, var(--pos-fg) 3%, transparent); }
.nui-table__row--clickable { cursor: pointer; }
.nui-table__row--clickable:hover { background: var(--pos-elevated); }
.nui-table__row--clickable:focus-visible {
  outline: none; box-shadow: inset 0 0 0 2px var(--pos-tint-soft);
}
.nui-table__td {
  padding: var(--pos-space-2) var(--pos-space-3);
  vertical-align: middle;
  border-left: none; border-right: none;
}
.nui-table__empty {
  padding: var(--pos-space-8); text-align: center;
  color: var(--pos-fg-tertiary); font-size: var(--pos-text-sm);
}
.nui-table__skel {
  display: block; height: 12px; width: 70%;
  border-radius: var(--pos-radius-xs);
  background: linear-gradient(90deg,
    var(--pos-elevated) 25%,
    color-mix(in oklch, var(--pos-fg) 8%, var(--pos-elevated)) 37%,
    var(--pos-elevated) 63%);
  background-size: 400% 100%;
  animation: nui-skel 1.4s ease infinite;
}
.nui-table__row--skeleton:hover { background: transparent; }
@keyframes nui-skel { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
@media (prefers-reduced-motion: reduce) { .nui-table__skel { animation: none; } }
.nui-table__foot {
  display: flex; align-items: center; justify-content: space-between;
  gap: var(--pos-space-3); flex-wrap: wrap;
  padding: var(--pos-space-2) var(--pos-space-3);
  border-top: 1px solid var(--pos-separator);
  background: var(--pos-surface);
}
.nui-table__count {
  font-size: var(--pos-text-xs); color: var(--pos-fg-tertiary);
  font-family: var(--font-mono, var(--font-sans));
  font-variant-numeric: tabular-nums;
}
.nui-table__pager { display: flex; align-items: center; gap: var(--pos-space-2); }
.nui-table__pageinfo {
  font-size: var(--pos-text-xs); color: var(--pos-fg-secondary);
  font-variant-numeric: tabular-nums; white-space: nowrap;
}
.nui-table__pagebtn {
  display: inline-flex; align-items: center; justify-content: center;
  min-height: 34px; padding: 0 var(--pos-space-3);
  border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md);
  background: var(--pos-elevated); color: var(--pos-fg);
  font-family: var(--font-display, var(--font-sans));
  font-size: var(--pos-text-xs); font-weight: var(--pos-weight-medium);
  cursor: pointer; -webkit-tap-highlight-color: transparent;
  transition: background var(--pos-duration-fast) var(--pos-ease-out);
}
.nui-table__pagebtn:hover:not(:disabled) { background: var(--pos-surface); }
.nui-table__pagebtn:disabled { opacity: .5; cursor: not-allowed; }
`
